import SendIcon from "@mui/icons-material/Send";
import { Alert, Box, Button, Chip, Grid, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import LeadCampaignApi from "../../../../../infrastructure/api/leadCampaignApi";
import MarketGrowthPageShell from "../components/MarketGrowthPageShell";
import MarketGrowthStatCard from "../components/MarketGrowthStatCard";
import {
  CAMPAIGN_STATUS_OPTIONS,
  CAMPAIGN_TYPE_OPTIONS,
  CHANNEL_OPTIONS,
  GOAL_OPTIONS,
  SEND_MODE_OPTIONS,
  getEnumLabel,
} from "../constants/leadCampaignEnums";
import CampaignRecipientsTable from "./components/CampaignRecipientsTable";
import PrepareBatchPanel from "./components/PrepareBatchPanel";
import SendCampaignDialog from "./components/SendCampaignDialog";
import { LeadCampaignActionsBar } from './LeadCampaignActionsBar';

const COMPLETED_STATUS = 4;
const CANCELLED_STATUS = 5;

const LeadCampaignDetailPage = () => {
  const { id } = useParams();
  const campaignId = Number(id);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [prepareResult, setPrepareResult] = useState(null);
  const [recipientsRefreshKey, setRecipientsRefreshKey] = useState(0);

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    setError(null);
    try {
      const [campaignRes, perfRes] = await Promise.all([
        LeadCampaignApi.getCampaignById(campaignId),
        LeadCampaignApi.getCampaignPerformance(campaignId),
      ]);
      const campaignNorm = LeadCampaignApi.normalize(campaignRes, "بارگذاری کمپین ناموفق بود.");
      const perfNorm = LeadCampaignApi.normalize(perfRes, "بارگذاری گزارش ناموفق بود.");
      if (campaignNorm.error) {
        setError(campaignNorm.error);
        return;
      }
      setCampaign(campaignNorm.data);
      setPerformance(perfNorm.data);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    load();
  }, [load]);

  const canSend = campaign?.canSend === true;
  const sendButtonLabel = "ارسال کمپین";

  // Action wrappers for the actions bar
  const handleRetry = async () => {
    await load();
  };

  const handleCancel = async () => {
    if (!campaignId) return;
    try {
      await LeadCampaignApi.patchCampaignStatus(campaignId, 5); // 5 = Cancelled
      toast.success("کمپین متوقف شد.");
      await load();
    } catch (e) {
      toast.error(e?.message || "خطا در توقف کمپین");
    }
  };

  const actionsOnPrepare = async () => {
    return handlePrepare({});
  };

  const actionsOnSend = async () => {
    return handleSend({});
  };

  const handlePrepare = async (payload) => {
    setPreparing(true);
    try {
      const res = await LeadCampaignApi.prepareBatch(campaignId, payload);
      const normalized = LeadCampaignApi.normalize(res, "آماده‌سازی دسته ناموفق بود.");
      if (normalized.error) return { error: normalized.error };
      setPrepareResult(normalized.data);
      toast.success(`${normalized.data?.selected ?? 0} مخاطب آماده شد.`);
      setRecipientsRefreshKey((k) => k + 1);
      await load();
      return {};
    } catch (e) {
      return { error: e?.message || "خطا در آماده‌سازی" };
    } finally {
      setPreparing(false);
    }
  };

  const handleSend = async (payload) => {
    setSending(true);
    try {
      const res = await LeadCampaignApi.sendCampaign(campaignId, payload);
      const normalized = LeadCampaignApi.normalize(res, "ارسال کمپین ناموفق بود.");
      if (normalized.error) return { error: normalized.error };
      const data = normalized.data || {};
      toast.success(
        data.message ||
        `${data.sent ?? 0} پیامک ارسال شد. ${data.remainingCount ?? 0} در صف باقی ماند.`
      );
      setSendOpen(false);
      setRecipientsRefreshKey((k) => k + 1);
      await load();
      return {};
    } catch (e) {
      return { error: e?.message || "خطا در ارسال" };
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <LeadCampaignActionsBar
        campaign={campaign}
        onSend={handleSend}
        onPrepare={handlePrepare}
        onRetry={handleRetry}
        onCancel={handleCancel}
      />

      <MarketGrowthPageShell
        title={campaign?.title || "جزئیات کمپین"}
        description="اطلاعات کمپین، آماده‌سازی دسته، ارسال و پیگیری گیرندگان."
        loading={loading}
        error={error}
        onRetry={load}
      >
        {campaign ? (
          <>
            <Box mb={2} display="flex" flexWrap="wrap" gap={1} alignItems="center">
              <Chip size="small" label={getEnumLabel(CAMPAIGN_STATUS_OPTIONS, campaign.status)} />
              <Chip size="small" label={getEnumLabel(CHANNEL_OPTIONS, campaign.channel)} />
              <Chip size="small" label={getEnumLabel(CAMPAIGN_TYPE_OPTIONS, campaign.campaignType)} />
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={getEnumLabel(SEND_MODE_OPTIONS, campaign.sendMode)}
              />
              {canSend ? (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SendIcon />}
                  onClick={() => setSendOpen(true)}
                  sx={{ mr: "auto" }}
                >
                  {sendButtonLabel}
                </Button>
              ) : null}
            </Box>
            {campaign.sendReason ? (
              <Alert severity={canSend ? "info" : "warning"} sx={{ mb: 2 }}>
                {campaign.sendReason}
              </Alert>
            ) : null}
            {campaign.autoExecutionStatus && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {campaign.autoExecutionStatus}
              </Alert>
            )}
            <Alert severity="warning" sx={{ mb: 2 }}>
              قبل از ارسال، ظرفیت تیم پاسخ‌گویی را بررسی کنید.
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              گروه: {campaign.groupTitle || campaign.groupId} | هدف:{" "}
              {getEnumLabel(GOAL_OPTIONS, campaign.goal)}
            </Typography>
            {campaign.smsText ? (
              <Typography variant="body2" sx={{ mb: 2 }}>
                متن پیامک: {campaign.smsText}
              </Typography>
            ) : null}

            <PrepareBatchPanel
              campaign={campaign}
              onPrepare={handlePrepare}
              preparing={preparing}
              lastResult={prepareResult}
            />

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <MarketGrowthStatCard label="آماده‌شده" value={campaign.recipientCount ?? 0} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MarketGrowthStatCard label="در صف ارسال" value={campaign.remainingCount ?? 0} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MarketGrowthStatCard label="ارسال‌شده" value={campaign.sentCount ?? 0} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MarketGrowthStatCard label="ناموفق" value={campaign.failedCount ?? 0} />
              </Grid>
            </Grid>

            <CampaignRecipientsTable
              campaignId={campaignId}
              refreshKey={recipientsRefreshKey}
            />

            {performance ? (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">عملکرد</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MarketGrowthStatCard label="مخاطبین" value={performance.contactCount ?? 0} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MarketGrowthStatCard label="ثبت‌نام" value={performance.registeredCount ?? 0} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MarketGrowthStatCard
                    label="درخواست پروژه"
                    value={performance.requestedCount ?? performance.projectRequestCount ?? 0}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MarketGrowthStatCard label="قرارداد" value={performance.contractedCount ?? 0} />
                </Grid>
              </Grid>
            ) : null}

            <SendCampaignDialog
              open={sendOpen}
              onClose={() => !sending && setSendOpen(false)}
              onConfirm={handleSend}
              campaign={campaign}
              sending={sending}
            />
          </>
        ) : null}
      </MarketGrowthPageShell>
    </>
  );
};

export default LeadCampaignDetailPage;
